import {
	createNewAgent,
	fetchAgents as fetchAgentsAPI,
	fetchAgent as fetchAgentAPI,
	deleteAgent as deleteAgentAPI,
	updateAgent as updateAgentAPI,
	fetchAgentEntityRelationData,
	fetchAgentEntityGraph,
	expandAgentEntityGraph,
} from "actions/fetch";

import { DATASET_ID_ENTITIES } from "layout/DataExplorer/DataExplorerConstants.js";
import { AGENTS_ROUTE } from "constants/constants.js";

import { entityViewNavigation } from "actions/navigation.js";

// General Agent Action Types
export const ADD_AGENT = "ADD_AGENT";
export const ADD_AGENT_SUCCESS = "ADD_AGENT_SUCCESS";
export const ADD_AGENT_FAILURE = "ADD_AGENT_FAILURE";
export const UPDATE_AGENT = "UPDATE_AGENT";
export const UPDATE_AGENT_SUCCESS = "UPDATE_AGENT_SUCCESS";
export const UPDATE_AGENT_FAILURE = "UPDATE_AGENT_FAILURE";
export const DELETE_AGENT = "DELETE_AGENT";
export const DELETE_AGENT_SUCCESS = "DELETE_AGENT_SUCCESS";
export const DELETE_AGENT_FAILURE = "DELETE_AGENT_FAILURE";
export const SET_AGENT_STATUS = "SET_AGENT_STATUS";
export const FETCH_AGENTS_REQUEST = "FETCH_AGENTS_REQUEST";
export const FETCH_AGENTS_SUCCESS = "FETCH_AGENTS_SUCCESS";
export const FETCH_AGENTS_FAILURE = "FETCH_AGENTS_FAILURE";
export const FETCH_AGENT_REQUEST = "FETCH_AGENT_REQUEST";
export const FETCH_AGENT_SUCCESS = "FETCH_AGENT_SUCCESS";
export const FETCH_AGENT_FAILURE = "FETCH_AGENT_FAILURE";
export const AGENT_SET_SELECTED_AGENT = "AGENT_SET_SELECTED_AGENT";

// Bio Agent Specific Action Types
export const AGENT_FETCH_ENTITY_RELATION_DATA = "AGENT_FETCH_ENTITY_RELATION_DATA";
export const AGENT_RECEIVE_ENTITY_RELATION_DATA = "AGENT_RECEIVE_ENTITY_RELATION_DATA";
export const AGENT_RECEIVE_ENTITY_RELATION_DATA_ERROR = "AGENT_RECEIVE_ENTITY_RELATION_DATA_ERROR";
export const AGENT_RECEIVE_ENTITY_RELATION_DATA_RETRY = "AGENT_RECEIVE_ENTITY_RELATION_DATA_RETRY";
export const AGENT_FETCH_GRAPH_DATA = "AGENT_FETCH_GRAPH_DATA";
export const AGENT_RECEIVE_GRAPH_DATA = "AGENT_RECEIVE_GRAPH_DATA";
export const AGENT_RECEIVE_GRAPH_DATA_ERROR = "AGENT_RECEIVE_GRAPH_DATA_ERROR";
export const AGENT_RECEIVE_GRAPH_DATA_RETRY = "AGENT_RECEIVE_GRAPH_DATA_RETRY";
export const AGENT_FETCH_EXPAND_GRAPH_DATA = "AGENT_FETCH_EXPAND_GRAPH_DATA";
export const AGENT_RECEIVE_EXPAND_GRAPH_DATA = "AGENT_RECEIVE_EXPAND_GRAPH_DATA";
export const AGENT_RECEIVE_EXPAND_GRAPH_DATA_ERROR = "AGENT_RECEIVE_EXPAND_GRAPH_DATA_ERROR";
export const AGENT_RECEIVE_EXPAND_GRAPH_DATA_RETRY = "AGENT_RECEIVE_EXPAND_GRAPH_DATA_RETRY";
export const AGENT_SET_GRAPH_DEPTH = "AGENT_SET_GRAPH_DEPTH";
export const AGENT_SET_GRAPH_EXPAND_DEPTH = "AGENT_SET_GRAPH_EXPAND_DEPTH";
export const AGENT_SET_GRAPH_SETTINGS_EXPANDED = "AGENT_SET_GRAPH_SETTINGS_EXPANDED";
export const AGENT_SET_GRAPH_FILTERS_EXPANDED = "AGENT_SET_GRAPH_FILTERS_EXPANDED";
export const AGENT_SET_ENTITIES_WIDGET_EXPANDED = "AGENT_SET_ENTITIES_WIDGET_EXPANDED";
export const AGENT_SET_AFFILIATIONS_WIDGET_EXPANDED = "AGENT_SET_AFFILIATIONS_WIDGET_EXPANDED";
export const AGENT_SET_CONFIG_WIDGET_EXPANDED = "AGENT_SET_CONFIG_WIDGET_EXPANDED";
export const AGENT_SET_ENTITIES_SORT = "AGENT_SET_ENTITIES_SORT";
export const AGENT_SET_ENTITIES_CURSOR_POS = "AGENT_SET_ENTITIES_CURSOR_POS";
export const AGENT_SET_EXPORT_OPTIONS = "AGENT_SET_EXPORT_OPTIONS";
export const AGENT_SET_SHOW_GRAPH_LABELS = "AGENT_SET_SHOW_GRAPH_LABELS";
export const AGENT_SET_SHOW_GRAPH_EDGE_LABELS = "AGENT_SET_SHOW_GRAPH_EDGE_LABELS";
export const AGENT_SET_GRAPH_LAYOUT_RUNNING = "AGENT_SET_GRAPH_LAYOUT_RUNNING";
export const AGENT_RESET_ALL_CURSOR_POS = "AGENT_RESET_ALL_CURSOR_POS";

export const addAgent = (agent) => {
	return async (dispatch, getState) => {
		dispatch({ type: ADD_AGENT, payload: agent });
		try {
			const response = await createNewAgent(agent)(dispatch, getState);
			dispatch({ type: ADD_AGENT_SUCCESS, payload: response });
			dispatch(fetchAgents());
			return response;
		} catch (error) {
			dispatch({ type: ADD_AGENT_FAILURE, error: error.message });
			throw error;
		}
	};
};

export const updateAgent = (agent) => {
	return async (dispatch, getState) => {
		dispatch({ type: UPDATE_AGENT, payload: agent });
		try {
			const response = await updateAgentAPI(agent)(dispatch, getState);
			dispatch({ type: UPDATE_AGENT_SUCCESS, payload: response });
			dispatch(fetchAgents());
			return response;
		} catch (error) {
			dispatch({ type: UPDATE_AGENT_FAILURE, payload: agent, error: error.message });
			throw error;
		}
	};
};

export const deleteAgent = (agentId) => {
	return async (dispatch, getState) => {
		dispatch({ type: DELETE_AGENT, payload: agentId });
		try {
			const response = await deleteAgentAPI(agentId)(dispatch, getState);
			dispatch({ type: DELETE_AGENT_SUCCESS, payload: response });
			dispatch(fetchAgents());
			return response;
		} catch (error) {
			dispatch({ type: DELETE_AGENT_FAILURE, error: error.message });
			throw error;
		}
	};
};

export const setAgentStatus = (agentId, status) => ({
	type: SET_AGENT_STATUS,
	payload: { agentId, status },
});

export const fetchAgents = () => {
	return async (dispatch, getState) => {
		dispatch({ type: FETCH_AGENTS_REQUEST });
		try {
			const response = await fetchAgentsAPI()(dispatch, getState);
			dispatch({ type: FETCH_AGENTS_SUCCESS, payload: response });
			return response;
		} catch (error) {
			dispatch({ type: FETCH_AGENTS_FAILURE, error: error.message });
			dispatch(handleAgentError(error));
			return null;
		}
	};
};

export const fetchAgent = (id) => {
	return async (dispatch, getState) => {
		dispatch({ type: FETCH_AGENT_REQUEST });
		try {
			const response = await fetchAgentAPI(id)(dispatch, getState);
			dispatch({ type: FETCH_AGENT_SUCCESS, payload: response });
			return response;
		} catch (error) {
			dispatch({ type: FETCH_AGENT_FAILURE, error: error.message });
			throw error;
		}
	};
};

export const setConfigWidgetExpanded = (expanded) => {
	return { type: AGENT_SET_CONFIG_WIDGET_EXPANDED, expanded };
};

export const setAffiliationsWidgetExpanded = (expanded) => {
	return { type: AGENT_SET_AFFILIATIONS_WIDGET_EXPANDED, expanded };
};

export const setEntitiesWidgetExpanded = (expanded) => {
	return { type: AGENT_SET_ENTITIES_WIDGET_EXPANDED, expanded };
};

export const setEntitiesCursorPosition = (pos, offset) => {
	return { type: AGENT_SET_ENTITIES_CURSOR_POS, pos, offset };
};

export const setGraphSettingsExpanded = (expanded) => {
	return { type: AGENT_SET_GRAPH_SETTINGS_EXPANDED, expanded };
};

export const setGraphFiltersExpanded = (expanded) => {
	return { type: AGENT_SET_GRAPH_FILTERS_EXPANDED, expanded };
};

export const updateEntityAffiliations = (entityId, agentId) => {
	return (dispatch, getState) => {
		const { graphDepth } = getState().agents.agentData.bio;
		dispatch(fetchAgentEntityGraph(entityId, agentId, graphDepth));
	};
};

export const setGraphDepth = (depth) => {
	return (dispatch, getState) => {
		const { selectedAgent } = getState().agents;
		dispatch({ type: AGENT_SET_GRAPH_DEPTH, depth });
		if (selectedAgent?.id) {
			dispatch(fetchAgentEntityGraph(selectedAgent.entity_id, selectedAgent.id, depth));
		}
	};
};

export const setGraphExpandDepth = (depth) => {
	return (dispatch) => {
		dispatch({ type: AGENT_SET_GRAPH_EXPAND_DEPTH, depth });
	};
};

export const navigateEntityNode = (entity) => {
	return (dispatch, getState) => {
		if (entity) {
			dispatch({ type: AGENT_RESET_ALL_CURSOR_POS });
			dispatch(entityViewNavigation(DATASET_ID_ENTITIES, entity, AGENTS_ROUTE));
		}
	};
};

export const expandEntityGraphByNode = (entity) => {
	return (dispatch, getState) => {
		const { graphExpandDepth } = getState().agents.agentData.bio;
		if (entity) {
			dispatch(expandAgentEntityGraph(entity, graphExpandDepth, entity.coords));
		}
	};
};

export const agentExpandEntityGraphByNode = (entity) => {
	return (dispatch, getState) => {
		if (entity) {
			// dispatch(expandEntityGraph(entity, graphExpandDepth, entity.coords));
		}
	};
};

export const getEntityRelationData = (edge) => {
	return (dispatch) => {
		if (edge) {
			dispatch(fetchAgentEntityRelationData(edge));
		}
	};
};

export const setSelectedAgent = (agent) => (dispatch, getState) => {
	dispatch({
		type: "AGENT_SET_SELECTED_AGENT",
		payload: agent,
	});
};

export const updateEntitiesByHeader = (columnId) => {
	return (dispatch, getState) => {
		const { entitiesSortType, entitiesSortAscending } = getState().agents.agentData.bio;
		const ascending = !columnId
			? entitiesSortAscending
			: columnId === entitiesSortType
				? !entitiesSortAscending
				: false;
		const sortBy = columnId || entitiesSortType;
		dispatch({
			type: AGENT_SET_ENTITIES_SORT,
			sortType: sortBy,
			sortAscending: ascending,
		});
	};
};

export const setExportOptions = (options) => {
	return (dispatch) => {
		dispatch({ type: AGENT_SET_EXPORT_OPTIONS, options });
	};
};

export const setShowGraphLabels = (show) => {
	return (dispatch) => {
		dispatch({ type: AGENT_SET_SHOW_GRAPH_LABELS, show });
	};
};

export const setShowGraphEdgeLabels = (show) => {
	return (dispatch) => {
		dispatch({ type: AGENT_SET_SHOW_GRAPH_EDGE_LABELS, show });
	};
};

export const setGraphLayoutRunning = (running) => {
	return (dispatch) => {
		dispatch({ type: AGENT_SET_GRAPH_LAYOUT_RUNNING, running });
	};
};

export const handleAgentError = (error) => {
	return (dispatch, getState) => {
		const throwError = () => {
			throw error;
		};
		dispatch({ type: "SET_ERROR_STATE", throwError });
	};
};
